import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Layers, Clock, TrendingUp, TrendingDown, Sparkles } from "lucide-react";
import { fmtUSD, gainClass } from "@/lib/format";

const fmtPx = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

export default function Options() {
  const [underlyings, setUnderlyings] = useState([]);
  const [underlying, setUnderlying] = useState("SPY");
  const [expirations, setExpirations] = useState([]);
  const [expiration, setExpiration] = useState(null);
  const [chain, setChain] = useState(null);
  const [side, setSide] = useState("call");
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState("1");
  const [busy, setBusy] = useState(false);
  const [portfolio, setPortfolio] = useState(null);
  const [loadingChain, setLoadingChain] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const loadPortfolio = useCallback(() => {
    api.get("/portfolio").then(({ data }) => setPortfolio(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/options/underlyings").then(({ data }) => setUnderlyings(data)).catch(() => {});
    loadPortfolio();
  }, [loadPortfolio]);

  useEffect(() => {
    setExpiration(null);
    setChain(null);
    setSelected(null);
    api
      .get("/options/expirations", { params: { underlying } })
      .then(({ data }) => {
        setExpirations(data.expirations || []);
        if (data.expirations?.length) setExpiration(data.expirations[0]);
      })
      .catch(() => setExpirations([]));
  }, [underlying]);

  const loadChain = useCallback(() => {
    if (!expiration) return;
    setLoadingChain(true);
    api
      .get("/options/chain", { params: { underlying, expiration } })
      .then(({ data }) => setChain(data))
      .catch(() => setChain(null))
      .finally(() => setLoadingChain(false));
  }, [underlying, expiration]);

  useEffect(() => {
    setSelected(null);
    loadChain();
    const t = setInterval(loadChain, 30000);
    return () => clearInterval(t);
  }, [loadChain]);

  const allRows = chain ? (side === "call" ? chain.calls : chain.puts) || [] : [];
  const rows = useMemo(() => {
    if (showAll || !chain?.underlying_price) return allRows;
    const lo = chain.underlying_price * 0.94;
    const hi = chain.underlying_price * 1.06;
    const near = allRows.filter((r) => r.strike >= lo && r.strike <= hi);
    return near.length >= 6 ? near : allRows;
  }, [allRows, showAll, chain]);

  const positions = portfolio?.positions || [];
  const optionPositions = positions.filter((p) => p.asset_type === "option");
  const netQty = useMemo(() => {
    const m = {};
    optionPositions.forEach((p) => (m[p.symbol] = p.direction === "short" ? -p.quantity : p.quantity));
    return m;
  }, [optionPositions]);
  const sharesHeld = (u) =>
    positions.find((p) => p.symbol === u && p.asset_type === "stock")?.quantity || 0;

  const nQty = Math.max(parseFloat(qty) || 0, 0);
  const estCost = selected ? selected.price * 100 * nQty : 0;
  const selNet = selected ? netQty[selected.contract_symbol] || 0 : 0;
  const sellOpens = selNet <= 0; // a sell would open/extend a short
  const buyCloses = selNet < 0;

  const place = async (orderSide, symbol, quantity) => {
    setBusy(true);
    try {
      await api.post("/trades", { symbol, asset_type: "option", side: orderSide, quantity });
      toast.success(
        orderSide === "buy"
          ? buyCloses ? "Bought to close" : "Bought to open"
          : sellOpens ? "Sold to open — premium collected" : "Sold to close"
      );
      loadPortfolio();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="options-root">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold flex items-center gap-2">
            <Layers size={22} /> Options
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Calls &amp; puts · long + covered calls &amp; cash-secured puts · 100 shares per contract
          </p>
        </div>
        <Badge variant="outline" className="rounded-full border-brand/30 text-brand-deep" data-testid="options-paper-badge">
          PAPER · Stage 2
        </Badge>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {underlyings.map((u) => (
          <button
            key={u.symbol}
            data-testid={`options-underlying-${u.symbol}`}
            onClick={() => setUnderlying(u.symbol)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              underlying === u.symbol
                ? "bg-brand text-white"
                : "bg-gray-100 text-gray-700 hover:bg-brand-tint"
            }`}
          >
            {u.symbol}
            <span className="ml-2 opacity-80">{u.price ? fmtUSD(u.price) : ""}</span>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border border-gray-200 rounded-2xl p-5" data-testid="options-chain-card">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-1 overflow-x-auto max-w-full pb-1" data-testid="options-expirations">
              {expirations.map((e) => (
                <button
                  key={e}
                  data-testid={`options-exp-${e}`}
                  onClick={() => setExpiration(e)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    expiration === e
                      ? "bg-brand-tint text-brand-deep ring-1 ring-brand/30"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {new Date(e + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 rounded-full bg-gray-100 p-1">
              {["call", "put"].map((s) => (
                <button
                  key={s}
                  data-testid={`options-side-${s}`}
                  onClick={() => { setSide(s); setSelected(null); }}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    side === s
                      ? s === "call" ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"
                      : "text-gray-600"
                  }`}
                >
                  {s === "call" ? "Calls" : "Puts"}
                </button>
              ))}
            </div>
          </div>

          {chain && (
            <p className="text-xs text-gray-500 mb-3" data-testid="options-chain-meta">
              {chain.name} at <span className="font-semibold">{fmtUSD(chain.underlying_price)}</span>
              {" · "}expires {new Date(chain.expiration + "T12:00:00").toLocaleDateString()} ({chain.days_to_expiration}d)
              {" · "}data: {chain.source === "yahoo" ? "Yahoo (delayed)" : "model estimate"}
              {allRows.length > rows.length || showAll ? (
                <button
                  data-testid="options-show-all-toggle"
                  onClick={() => setShowAll((v) => !v)}
                  className="ml-2 text-brand hover:underline font-medium"
                >
                  {showAll ? "Near the money only" : `Show all ${allRows.length} strikes`}
                </button>
              ) : null}
            </p>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                  <th className="py-2 pr-2">Strike</th>
                  <th className="py-2 pr-2">Bid</th>
                  <th className="py-2 pr-2">Ask</th>
                  <th className="py-2 pr-2">Mid</th>
                  <th className="py-2 pr-2">IV</th>
                  <th className="py-2 pr-2">Δ</th>
                  <th className="py-2 pr-2">Γ</th>
                  <th className="py-2 pr-2">Θ</th>
                  <th className="py-2 pr-2">V</th>
                  <th className="py-2 pr-2">OI</th>
                </tr>
              </thead>
              <tbody data-testid="options-chain-rows">
                {loadingChain && !rows.length ? (
                  <tr><td colSpan={10} className="py-8 text-center text-gray-400">Loading chain…</td></tr>
                ) : rows.length ? (
                  rows.map((r) => (
                    <tr
                      key={r.contract_symbol}
                      data-testid={`chain-row-${r.strike}`}
                      onClick={() => setSelected(r)}
                      className={`cursor-pointer border-b border-gray-50 transition-colors ${
                        selected?.contract_symbol === r.contract_symbol
                          ? "bg-brand-tint"
                          : r.in_the_money ? "bg-amber-50/60 hover:bg-brand-tint/60" : "hover:bg-brand-tint/60"
                      }`}
                    >
                      <td className="py-2 pr-2 font-semibold">
                        ${r.strike}
                        {r.in_the_money && <span className="ml-1.5 text-[10px] text-amber-600 font-bold">ITM</span>}
                      </td>
                      <td className="py-2 pr-2">{fmtPx(r.bid)}</td>
                      <td className="py-2 pr-2">{fmtPx(r.ask)}</td>
                      <td className="py-2 pr-2 font-medium">{fmtPx(r.price)}</td>
                      <td className="py-2 pr-2">{r.iv ? `${(r.iv * 100).toFixed(1)}%` : "—"}</td>
                      <td className="py-2 pr-2">{r.delta ?? "—"}</td>
                      <td className="py-2 pr-2 text-gray-500">{r.gamma ?? "—"}</td>
                      <td className="py-2 pr-2 text-gray-500">{r.theta ?? "—"}</td>
                      <td className="py-2 pr-2 text-gray-500">{r.vega ?? "—"}</td>
                      <td className="py-2 pr-2 text-gray-500">{r.open_interest?.toLocaleString() ?? "—"}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={10} className="py-8 text-center text-gray-400">No contracts for this expiration.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="border border-gray-200 rounded-2xl p-5" data-testid="options-ticket">
            <h2 className="text-base font-semibold mb-3">Order ticket</h2>
            {selected ? (
              <div className="space-y-3">
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="font-semibold text-sm" data-testid="ticket-contract-label">
                    {underlying} ${selected.strike} {side === "call" ? "Call" : "Put"}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Exp {expiration} · premium {fmtPx(selected.price)}/share
                  </p>
                  <div className="grid grid-cols-4 gap-1 mt-2 text-center" data-testid="ticket-greeks">
                    {[["Δ", selected.delta], ["Γ", selected.gamma], ["Θ/day", selected.theta], ["Vega", selected.vega]].map(([k, v]) => (
                      <div key={k} className="rounded-lg bg-white border border-gray-100 py-1">
                        <p className="text-[10px] text-gray-400">{k}</p>
                        <p className="text-xs font-semibold">{v ?? "—"}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-2 font-mono break-all">{selected.contract_symbol}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Contracts</label>
                  <Input
                    data-testid="options-qty-input"
                    type="number" min="1" step="1" value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Est. {sellOpens ? "credit if sold" : "cost"}</span>
                  <span className="font-semibold" data-testid="options-est-cost">{fmtUSD(estCost)}</span>
                </div>
                <Button
                  data-testid={buyCloses ? "buy-to-close-btn" : "buy-to-open-btn"}
                  disabled={busy || nQty <= 0}
                  onClick={() => place("buy", selected.contract_symbol, buyCloses ? Math.min(nQty, -selNet) : nQty)}
                  className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <TrendingUp size={14} className="mr-2" />
                  {buyCloses ? `Buy to close (${-selNet} short)` : "Buy to open"}
                </Button>
                <Button
                  data-testid={sellOpens ? "sell-to-open-btn" : "sell-to-close-btn"}
                  disabled={busy || nQty <= 0}
                  onClick={() => place("sell", selected.contract_symbol, sellOpens ? nQty : Math.min(nQty, selNet))}
                  variant="outline"
                  className="w-full rounded-full border-rose-300 text-rose-600 hover:bg-rose-50"
                >
                  <TrendingDown size={14} className="mr-2" />
                  {sellOpens ? "Sell to open (collect premium)" : `Sell to close (${selNet} held)`}
                </Button>
                {sellOpens && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg p-2" data-testid="collateral-hint">
                    {side === "call"
                      ? `Covered call: needs ${nQty * 100} ${underlying} shares (you hold ${Math.floor(sharesHeld(underlying))}).`
                      : `Cash-secured put: reserves ${fmtUSD(selected.strike * 100 * nQty)} collateral.`}
                  </p>
                )}
                <p className="text-[11px] text-gray-400">
                  Cash: {portfolio ? fmtUSD(portfolio.cash_balance) : "—"}
                  {portfolio?.options_collateral > 0 && (
                    <span data-testid="options-collateral-line"> · CSP collateral reserved: {fmtUSD(portfolio.options_collateral)}</span>
                  )}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500" data-testid="ticket-empty">
                Select a contract from the chain to build an order.
              </p>
            )}
          </Card>

          <Card className="border border-gray-200 rounded-2xl p-5" data-testid="options-positions-card">
            <h2 className="text-base font-semibold mb-3">Your option positions</h2>
            {optionPositions.length ? (
              <div className="divide-y divide-gray-100">
                {optionPositions.map((p) => (
                  <div key={p.symbol} className="py-3" data-testid={`option-position-${p.symbol}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold flex items-center gap-1.5">
                        {p.label || p.symbol}
                        {p.direction === "short" && (
                          <span className="text-[10px] font-bold text-rose-600 bg-rose-50 rounded-full px-1.5 py-0.5" data-testid={`option-short-badge-${p.symbol}`}>
                            SHORT
                          </span>
                        )}
                      </p>
                      <p className={`text-sm font-semibold ${gainClass(p.unrealized_pnl)}`}>
                        {p.unrealized_pnl >= 0 ? "+" : ""}{fmtUSD(p.unrealized_pnl)}
                      </p>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <p className="text-xs text-gray-500">
                        {p.quantity}x @ {fmtPx(p.avg_price)} · now {fmtPx(p.current_price)}
                      </p>
                      {p.days_to_expiration != null && (
                        <span
                          className={`text-[11px] flex items-center gap-1 ${
                            p.days_to_expiration <= 3 ? "text-amber-600 font-semibold" : "text-gray-400"
                          }`}
                          data-testid={`option-dte-${p.symbol}`}
                        >
                          <Clock size={11} /> {p.days_to_expiration}d
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      data-testid={`close-option-${p.symbol}`}
                      onClick={() => place(p.direction === "short" ? "buy" : "sell", p.symbol, p.quantity)}
                      className="mt-1.5 h-7 rounded-full text-xs text-rose-600 hover:bg-rose-50 px-3"
                    >
                      {p.direction === "short" ? "Buy back" : "Close position"}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500" data-testid="options-no-positions">
                No option positions yet. Expired contracts auto-settle; short ITM contracts get assigned.
              </p>
            )}
          </Card>
        </div>
      </div>

      <OptionsInsightsCard />
    </div>
  );
}

function OptionsInsightsCard() {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/options/insights").then(({ data }) => setDoc(data?.insights ? data : null)).catch(() => {});
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/options/insights");
      setDoc(data);
      toast.success("Options habits analyzed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const s = doc?.stats;
  const ins = doc?.insights;
  return (
    <Card className="border border-gray-200 rounded-2xl p-6" data-testid="options-insights-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Sparkles size={16} className="text-brand" /> AI options coach
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Premium risk · theta decay · moneyness habits — analyzed by Claude from your option fills
          </p>
        </div>
        <Button
          data-testid="options-insights-generate"
          onClick={generate}
          disabled={busy}
          className="rounded-full bg-brand text-white hover:bg-brand-deep"
        >
          {busy ? "Analyzing…" : doc ? "Re-analyze" : "Analyze my options habits"}
        </Button>
      </div>

      {s && (
        <p className="text-xs text-gray-600 mt-4" data-testid="options-insights-stats">
          {s.fills} fills · {s.long_opens} long / {s.short_opens} short opens · win rate {s.win_rate}% ·
          realized <span className={gainClass(s.realized_pnl)}>{fmtUSD(s.realized_pnl)}</span> ·
          premium paid {fmtUSD(s.premium_paid)} / received {fmtUSD(s.premium_received)}
          {s.avg_dte_at_entry != null && <> · avg {s.avg_dte_at_entry} DTE at entry</>}
          {" · "}moneyness {s.moneyness_at_entry.ITM} ITM / {s.moneyness_at_entry.ATM} ATM / {s.moneyness_at_entry.OTM} OTM
          {s.expired_worthless > 0 && <> · {s.expired_worthless} expired worthless</>}
          {s.assignments > 0 && <> · {s.assignments} assignments</>}
        </p>
      )}

      {ins ? (
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <div data-testid="insights-summary">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Summary</p>
            <p className="text-sm text-gray-700 leading-relaxed">{ins.summary}</p>
          </div>
          <div data-testid="insights-habits">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Observed habits</p>
            <ul className="space-y-1.5">
              {(ins.habits || []).map((h, i) => (
                <li key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-amber-500">•</span>{h}</li>
              ))}
              {!ins.habits?.length && <li className="text-sm text-gray-400">—</li>}
            </ul>
          </div>
          <div data-testid="insights-recommendations">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Recommendations</p>
            <ul className="space-y-1.5">
              {(ins.recommendations || []).map((r, i) => (
                <li key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-brand">•</span>{r}</li>
              ))}
              {!ins.recommendations?.length && <li className="text-sm text-gray-400">—</li>}
            </ul>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-400 mt-4" data-testid="options-insights-empty">
          No analysis yet — place a few option trades, then run the coach.
        </p>
      )}
    </Card>
  );
}
